import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PMDialog } from "../../../utils/PMDialog";
import {
  FOLDER_ACCESS_PROJECT,
  FolderAccessFields,
  folderEffectiveAccess,
} from "./folder-access-fields";

export function folderRecord(folder) {
  if (!folder) return null;
  const raw = folder.object || folder;
  return {
    id: raw.id || folder.id,
    title: raw.title || raw.displayTitle || folder.name || folder.title || "",
    description: raw.description || "",
    access: raw.access || folder.access || FOLDER_ACCESS_PROJECT,
    effective_access: folderEffectiveAccess(raw),
    parent_id: raw.parent_id ?? raw.parent?.id ?? null,
  };
}

export function FolderRenameDialog({
  open,
  folder,
  saving = false,
  onClose,
  onSave,
}) {
  const { t } = useTranslation();
  const record = folderRecord(folder);
  const [title, setTitle] = useState(record?.title || "");

  useEffect(() => {
    setTitle(record?.title || "");
  }, [record?.id, record?.title, open]);

  if (!record) return null;

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      size="sm"
      title="document_archive.rename_folder"
      closeOnOutsideClick
      panelClassName="rounded-[22px] border border-[#EDF0EF]"
    >
      <form
        className="flex flex-col gap-4 px-1 pb-1 text-left"
        onSubmit={(event) => {
          event.preventDefault();
          onSave?.(title.trim());
        }}
      >
        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-medium text-[#4A5A57]">
            {t("document_archive.folder_title")}
          </span>
          <input
            autoFocus
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder={t("document_archive.folder_title_placeholder")}
            className="h-11 rounded-[16px] border border-[#E5E9E8] bg-[#F5F7F6] px-4 text-sm text-[#10221F] outline-none placeholder:text-[#93A19E] focus:border-[#0F766E] focus:bg-white"
          />
        </label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex h-10 flex-1 items-center justify-center rounded-[20px] border border-[#E5E9E8] text-sm font-medium text-[#4A5A57]"
          >
            {t("document_archive.cancel")}
          </button>
          <button
            type="submit"
            disabled={saving || !title.trim()}
            className="flex h-10 flex-1 items-center justify-center rounded-[20px] bg-[#0F766E] text-sm font-semibold text-white disabled:bg-[#A6B2AF]"
          >
            {saving ? t("document_archive.saving") : t("document_archive.save")}
          </button>
        </div>
      </form>
    </PMDialog>
  );
}

export function FolderAccessDialog({
  open,
  folder,
  saving = false,
  inherited = false,
  onClose,
  onSave,
}) {
  const record = folderRecord(folder);
  if (!record) return null;

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      size="md"
      title="document_archive.folder_access"
      closeOnOutsideClick
      panelClassName="rounded-[22px] border border-[#EDF0EF]"
    >
      <div className="px-1 pb-2 text-left">
        <FolderAccessFields
          value={record.access}
          inherited={inherited}
          disabled={saving || inherited}
          onChange={(access) => onSave?.(access)}
        />
      </div>
    </PMDialog>
  );
}

export function FolderDeleteDialog({
  open,
  folder,
  childCount = 0,
  fileCount = 0,
  destinationName,
  deleting = false,
  onClose,
  onConfirm,
}) {
  const { t } = useTranslation();
  const record = folderRecord(folder);
  if (!record) return null;

  const destination =
    destinationName || t("document_archive.folder_delete_root");

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      size="sm"
      title="document_archive.delete_folder_title"
      closeOnOutsideClick
      panelClassName="rounded-[22px] border border-[#EDF0EF]"
    >
      <div className="px-1 pb-1 text-left">
        <p className="text-sm text-[#6B7B78]">
          {childCount > 0
            ? t("document_archive.delete_folder_confirm_children", {
                name: record.title,
                count: childCount,
                destination,
                files: fileCount,
              })
            : t("document_archive.delete_folder_confirm", {
                name: record.title,
                destination,
                files: fileCount,
              })}
        </p>
        <div className="mt-5 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex h-10 flex-1 items-center justify-center rounded-[20px] border border-[#E5E9E8] text-sm font-medium text-[#4A5A57]"
          >
            {t("document_archive.cancel")}
          </button>
          <button
            type="button"
            disabled={deleting}
            onClick={onConfirm}
            className="flex h-10 flex-1 items-center justify-center rounded-[20px] bg-red-600 text-sm font-semibold text-white hover:bg-red-700 disabled:bg-red-300"
          >
            {deleting
              ? t("document_archive.deleting")
              : t("document_archive.delete_folder")}
          </button>
        </div>
      </div>
    </PMDialog>
  );
}
